import { 
  FaUser, 
  FaGraduationCap, 
  FaBrain, 
  FaLightbulb, 
  FaRocket 
} from 'react-icons/fa';
import { Step } from '../types';

// Added new traits for clarity:
//  - hands_on: indicates enjoyment of physical/mechanical tasks
//  - spatial: indicates good spatial reasoning (e.g., mechanical design)
//  - logical: indicates algorithmic reasoning (coding, logic-based problem solving)
//  - systematic: emphasizes methodical approaches, step-by-step logic (common in software)

export const steps: Step[] = [
  {
    title: "Personality & Values",
    icon: FaUser,
    description:
      "Uncover key aspects of your personality and core work values. This step helps us understand your motivational drivers.",
    color: "#8E24AA",
    questions: [
      {
        question: "How would you describe your social preference in a work or learning environment?",
        explanation:
          "This measures your comfort with teamwork vs. independent work, hinting at roles where you'll thrive.",
        options: [
          {
            text: "I prefer to work alone with minimal interaction.",
            traits: { analytical: 1, detail_oriented: 1, logical: 1 }
          },
          {
            text: "I enjoy working independently, but occasional collaboration is fine.",
            traits: { analytical: 1, adaptable: 1, logical: 1 }
          },
          {
            text: "A mix of alone time and teamwork is ideal.",
            traits: { analytical: 1, social: 1 }
          },
          {
            text: "I’m happier in moderate social or team-based settings.",
            traits: { social: 2 }
          },
          {
            text: "I thrive in highly collaborative, group-focused environments.",
            traits: { social: 2, leadership: 1 }
          }
        ],
        careers: []
      },
      {
        question: "What motivates you the most when pursuing a project or goal?",
        explanation:
          "Identifying core drivers (financial, social impact, innovation, recognition, learning) can guide career fit.",
        options: [
          {
            text: "Achieving financial rewards and tangible success",
            traits: { strategic: 2 } 
          },
          {
            text: "Making a positive social impact on the community",
            traits: { helping: 2, service_oriented: 1 }
          },
          {
            text: "Innovating or exploring uncharted territory",
            traits: { innovative: 2, exploratory: 1 }
          },
          {
            text: "Gaining recognition and leadership opportunities",
            traits: { leadership: 2, articulate: 1 }
          },
          {
            text: "Continual learning and personal growth",
            traits: { analytical: 1, innovative: 1 }
          }
        ],
        careers: []
      },
      {
        question: "How comfortable are you with uncertainty or risk in projects?",
        explanation:
          "Risk tolerance can suggest whether entrepreneurial or structured environments suit you best.",
        options: [
          {
            text: "I avoid risk and prefer clear, well-defined tasks.",
            traits: { detail_oriented: 2, ethical: 1 }
          },
          {
            text: "I can handle small risks if the rewards are worthwhile.",
            traits: { analytical: 1 }
          },
          {
            text: "I’m okay with moderate uncertainty as long as the goals are clear.",
            traits: { adaptable: 1, innovative: 1 }
          },
          {
            text: "I enjoy taking on new challenges and adapting to changes.",
            traits: { innovative: 2, adaptable: 2 }
          },
          {
            text: "I thrive in unpredictable environments—risk excites me.",
            traits: { innovative: 2, exploratory: 2 }
          }
        ],
        careers: []
      }
    ]
  },
  {
    title: "Academic Interests",
    icon: FaGraduationCap,
    description:
      "Explore your favorite subjects and learning styles to understand where your natural curiosities lie.",
    color: "#1976D2",
    questions: [
      {
        question: "Which subjects do you consistently excel in and find engaging?",
        explanation:
          "Reflect on classes where you naturally do well—this can indicate future career alignment.",
        options: [
          {
            text: "Mathematics & Physical Sciences (Physics, Chemistry)",
            traits: { technical: 2, analytical: 2, scientific: 2, spatial: 1 }
          },
          {
            text: "Biology & Life Sciences",
            traits: { scientific: 2, detail_oriented: 1 }
          },
          {
            text: "Computer Science & Technology",
            traits: { technical: 2, innovative: 1, logical: 2 }
          },
          {
            text: "Humanities & Social Sciences (History, Literature, Economics)",
            traits: { analytical: 1, social: 1, articulate: 1 }
          },
          {
            text: "Arts & Creative Subjects",
            traits: { artistic: 2, creative: 2 }
          },
          {
            text: "Business & Economics",
            traits: { strategic: 2, analytical: 1 }
          }
        ],
        careers: []
      },
      {
        question: "Which type of tasks in these subjects do you enjoy the most?",
        explanation:
          "Projects can range from problem sets and lab work to presentations or creative endeavors.",
        options: [
          {
            text: "Problem-solving sets and logical puzzles",
            traits: { analytical: 2, logical: 1 }
          },
          {
            text: "Writing essays or conducting interviews",
            traits: { articulate: 2 }
          },
          {
            text: "Designing creative projects (posters, apps, prototypes)",
            traits: { creative: 2, innovative: 1 }
          },
          {
            text: "Lab experiments or field research",
            traits: { scientific: 2, exploratory: 1, hands_on: 1 }
          },
          {
            text: "Collaborative team-based assignments",
            traits: { social: 2, leadership: 1 }
          }
        ],
        careers: []
      },
      {
        question: "How do you feel about theoretical vs. applied learning?",
        explanation:
          "Some fields are heavily theory-driven, while others are more hands-on.",
        options: [
          {
            text: "Strongly prefer hands-on, practical experiences",
            traits: { exploratory: 2, technical: 1, hands_on: 2 }
          },
          {
            text: "A balanced mix of theory and practice is ideal",
            traits: { analytical: 1, technical: 1 }
          },
          {
            text: "I enjoy deep theoretical exploration over applied tasks",
            traits: { analytical: 2 }
          },
          {
            text: "I prefer reading, analyzing, and writing (research focus)",
            traits: { analytical: 2, articulate: 1 }
          },
          {
            text: "I learn best through real-world case studies or field work",
            traits: { exploratory: 1, social: 1, hands_on: 1 }
          }
        ],
        careers: []
      }
    ]
  },
  {
    title: "Skills Assessment",
    icon: FaBrain,
    description:
      "Identify your strongest competencies, both technical and soft skills, to gauge potential career matches.",
    color: "#0277BD",
    questions: [
      {
        question: "Which technical or specialized skills are you most confident in?",
        explanation:
          "Consider areas where you have training, certifications, or significant experience.",
        options: [
          {
            text: "Advanced Mathematics (Calculus, Statistics)",
            traits: { analytical: 2, logical: 1 }
          },
          {
            text: "Programming (Python, Java, Web Dev)",
            traits: { technical: 2, logical: 2, systematic: 1 }
          },
          {
            text: "Scientific Lab Techniques (experiments, data analysis)",
            traits: { scientific: 2, detail_oriented: 1, hands_on: 1 }
          },
          {
            text: "Writing & Editing (journalism, creative writing, academic papers)",
            traits: { articulate: 2 }
          },
          {
            text: "Artistic Design (digital art, illustration, 3D modeling)",
            traits: { creative: 2, artistic: 2 }
          },
          {
            text: "Business Analytics (Excel, market research, data interpretation)",
            traits: { analytical: 1, strategic: 2 }
          }
        ],
        careers: []
      },
      {
        question: "Which soft skills do others often notice or praise in you?",
        explanation:
          "Soft skills can be critical to workplace success—leadership, communication, empathy, and more.",
        options: [
          {
            text: "Leadership & Decision-Making",
            traits: { leadership: 2, strategic: 1 }
          },
          {
            text: "Persuasive Communication & Negotiation",
            traits: { articulate: 2, social: 1 }
          },
          {
            text: "Creative Problem Solving & Brainstorming",
            traits: { creative: 2, innovative: 1 }
          },
          {
            text: "Organization & Time Management",
            traits: { detail_oriented: 2, analytical: 1, systematic: 1 }
          },
          {
            text: "Conflict Resolution & Empathy",
            traits: { helping: 2, social: 1 }
          }
        ],
        careers: []
      },
      {
        question: "How do you respond to a difficult or unexpected problem?",
        explanation:
          "Your default strategy for tackling challenges can reveal suitability for certain roles.",
        options: [
          {
            text: "Break it into smaller parts and solve methodically",
            traits: { analytical: 2, detail_oriented: 1, systematic: 1 }
          },
          {
            text: "Collaborate with peers to brainstorm solutions",
            traits: { social: 2, leadership: 1 }
          },
          {
            text: "Thoroughly research expert insights before acting",
            traits: { analytical: 1, scientific: 1 }
          },
          {
            text: "Use creativity or intuition to propose novel ideas quickly",
            traits: { creative: 2, innovative: 1 }
          },
          {
            text: "Experiment with different approaches and iterate as I go",
            traits: { exploratory: 2, adaptable: 1 }
          }
        ],
        careers: []
      }
    ]
  },
  {
    title: "Work Style & Preferences",
    icon: FaLightbulb,
    description:
      "Discover the work environments and roles that energize you. This informs how you perform in different professional settings.",
    color: "#00695C",
    questions: [
      {
        question: "How do you typically prefer to organize your day?",
        explanation:
          "Some people need strict routines, while others thrive on flexibility and spontaneity.",
        options: [
          {
            text: "A strict schedule with clearly defined goals",
            traits: { detail_oriented: 2, analytical: 1, systematic: 1 }
          },
          {
            text: "A somewhat flexible routine that accommodates creativity",
            traits: { adaptable: 2, creative: 1 }
          },
          {
            text: "Ad-hoc, guided by immediate priorities",
            traits: { adaptable: 2 }
          },
          {
            text: "Adapting quickly to changing tasks and challenges",
            traits: { adaptable: 2, innovative: 1 }
          },
          {
            text: "Team-based planning with delegated tasks",
            traits: { social: 2, leadership: 1 }
          }
        ],
        careers: []
      },
      {
        question: "Which parts of a job or project do you find most fulfilling?",
        explanation:
          "Pinpoint tasks that you love can help match you with careers that keep you motivated.",
        options: [
          {
            text: "Innovating technical solutions or building prototypes",
            traits: { technical: 2, innovative: 2, hands_on: 2 }
          },
          {
            text: "Helping individuals directly (social work, healthcare)",
            traits: { helping: 2, service_oriented: 2 }
          },
          {
            text: "Brainstorming creative concepts and designs",
            traits: { creative: 2, artistic: 1 }
          },
          {
            text: "Analyzing data to find insights or patterns",
            traits: { analytical: 2, scientific: 1 }
          },
          {
            text: "Leading or organizing large-scale initiatives",
            traits: { leadership: 2, strategic: 1 }
          }
        ],
        careers: []
      },
      {
        question: "How do you cope with high-pressure or stressful situations?",
        explanation:
          "Stress management strategies can indicate how you'll handle careers with tight deadlines or uncertainty.",
        options: [
          {
            text: "Plan meticulously to minimize surprises",
            traits: { detail_oriented: 2, systematic: 1 }
          },
          {
            text: "Seek support from colleagues or mentors",
            traits: { social: 2 }
          },
          {
            text: "Use creative outlets to manage stress",
            traits: { creative: 2 }
          },
          {
            text: "Thrive under tight deadlines and last-minute changes",
            traits: { innovative: 2, adaptable: 2 }
          },
          {
            text: "Maintain work-life balance and focus on mental well-being",
            traits: { ethical: 2 }
          }
        ],
        careers: []
      },
      {
        question: "What level of structure and supervision do you prefer in your day-to-day tasks?",
        explanation:
          "Some thrive under clear guidelines, while others prefer autonomy or leadership roles.",
        options: [
          {
            text: "I need clear directives and defined procedures",
            traits: { detail_oriented: 2, systematic: 1 }
          },
          {
            text: "I work best with some guidance, but also autonomy to decide details",
            traits: { analytical: 1, adaptable: 1 }
          },
          {
            text: "I prefer to self-manage and set my own tasks",
            traits: { leadership: 1, innovative: 1 }
          },
          {
            text: "I enjoy a leadership role, guiding others in a structured way",
            traits: { leadership: 2, strategic: 1 }
          },
          {
            text: "I like a collaborative environment where tasks are dynamic",
            traits: { social: 2, adaptable: 1 }
          }
        ],
        careers: []
      }
    ]
  },
  {
    title: "Future Goals & Motivations",
    icon: FaRocket,
    description:
      "Reflect on your aspirations and the impact you wish to make, ensuring your career aligns with your long-term vision.",
    color: "#283593",
    questions: [
      {
        question: "Where do you hope to be 5–10 years from now?",
        explanation:
          "Do you see yourself as a technical expert, manager, researcher, entrepreneur, or social impact leader?",
        options: [
          {
            text: "Working as a leading expert or specialist in my field",
            traits: { technical: 2, analytical: 2 }
          },
          {
            text: "Managing teams or heading a department/company",
            traits: { leadership: 2, strategic: 2 }
          },
          {
            text: "Conducting research or teaching in an academic setting",
            traits: { scientific: 2, analytical: 1 }
          },
          {
            text: "Running a startup or launching innovative products",
            traits: { innovative: 2, leadership: 1 }
          },
          {
            text: "Driving social change through community-based initiatives",
            traits: { helping: 2, service_oriented: 2 }
          }
        ],
        careers: []
      },
      {
        question: "Which global or societal challenges do you feel most compelled to address?",
        explanation:
          "Highlighting these interests can steer you toward careers that focus on these issues.",
        options: [
          {
            text: "Climate change and environmental sustainability",
            traits: { scientific: 2, service_oriented: 1 }
          },
          {
            text: "Healthcare access and medical innovation",
            traits: { helping: 2, scientific: 1 }
          },
          {
            text: "Technological advancement (AI, robotics, data privacy)",
            traits: { technical: 2, innovative: 1, logical: 1 }
          },
          {
            text: "Education reform and equal opportunities",
            traits: { social: 1, articulate: 1 }
          },
          {
            text: "Social justice and diversity, equity, and inclusion",
            traits: { helping: 2, ethical: 2 }
          }
        ],
        careers: []
      },
      {
        question: "Which factors are the biggest deal-breakers for you in a career?",
        explanation:
          "A job might be perfect on paper but fail if it clashes with your values or lifestyle needs.",
        options: [
          {
            text: "Low compensation or minimal financial growth",
            traits: {}
          },
          {
            text: "Limited creativity or repetitive tasks",
            traits: {}
          },
          {
            text: "Poor work-life balance (excessive overtime, burnout)",
            traits: {}
          },
          {
            text: "Lack of social impact or meaning",
            traits: {}
          },
          {
            text: "Minimal learning or personal growth",
            traits: {}
          }
        ],
        careers: []
      },
      {
        question: "Which phrase resonates most with your definition of success?",
        explanation:
          "This question aims to capture your intrinsic motivation—status, innovation, impact, etc.",
        options: [
          {
            text: "Climbing the ladder and becoming a recognized leader",
            traits: { leadership: 2, strategic: 2 }
          },
          {
            text: "Innovating to create something entirely new",
            traits: { innovative: 2, creative: 1 }
          },
          {
            text: "Working for the greater good of society",
            traits: { service_oriented: 2, helping: 1 }
          },
          {
            text: "Achieving financial freedom and stability",
            traits: { strategic: 2 }
          },
          {
            text: "Expressing myself creatively without bounds",
            traits: { artistic: 2, creative: 2 }
          }
        ],
        careers: []
      }
    ]
  }
];
